import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Loader2, UserPlus } from 'lucide-react'
import { toast } from 'sonner'

interface AddProfileModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddProfileModal({ open, onOpenChange }: AddProfileModalProps) {
  const [name, setName] = useState('')
  const [cookies, setCookies] = useState('')
  const qc = useQueryClient()

  const createProfile = useMutation({
    mutationFn: (data: { name: string; cookies_json: string }) =>
      api.post<{ ok: boolean }>('/automacao-fb/profiles', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['automacao-fb'] })
      toast.success('Perfil adicionado com sucesso')
      setName('')
      setCookies('')
      onOpenChange(false)
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Erro ao adicionar perfil')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Nome e obrigatorio')
      return
    }
    if (!cookies.trim()) {
      toast.error('Cookies sao obrigatorios')
      return
    }
    createProfile.mutate({ name: name.trim(), cookies_json: cookies.trim() })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111113] border-[#27272a] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-zinc-100">
            <UserPlus className="h-5 w-5 text-indigo-400" />
            Adicionar Perfil
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Adicione um perfil do Facebook para automacao. Cole os cookies JSON da sessao.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs font-medium text-zinc-400">Nome do Perfil</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Perfil Principal"
              className="bg-[#09090b] border-[#27272a] text-zinc-100 placeholder:text-zinc-600 focus-visible:ring-indigo-500/30"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-medium text-zinc-400">Cookies JSON</label>
            <textarea
              value={cookies}
              onChange={(e) => setCookies(e.target.value)}
              placeholder='[{"name": "c_user", "value": "...", ...}]'
              rows={6}
              className="w-full rounded-md bg-[#09090b] border border-[#27272a] px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 font-mono resize-none"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="border-[#27272a] text-zinc-400 hover:bg-[#18181b] hover:text-zinc-200"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={createProfile.isPending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {createProfile.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
